'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { AlertDialog } from '@workspace/ui/components/custom/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { cn, formatAmount } from '@workspace/ui/lib';
import type { Currency } from '@workspace/ui/types';
import { ChevronDown, EyeOff, MoreVertical, Star } from 'lucide-react';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AddNewCurrencyDialog } from '@/components/wallet/add-new-currency-dialog';
import { useUpdateWalletSort, type WalletSort } from '@/hooks/use-settings';
import { useSetPrimaryWallet, useSetWalletVisibility } from '@/hooks/use-wallet';
import type { Wallet } from '@/types/wallet';

interface CurrencySwitcherProps {
  currency: Currency;
  activatedCurrencies: Currency[];
  availableCurrencies: Currency[];
  hiddenCurrencies: Currency[];
  walletSort: WalletSort;
  wallets: Wallet[];
  onCurrencySelect: (currencyCode: string) => Promise<void>;
  isRequestingWallet: boolean;
  walletsLoading: boolean;
}

interface PendingPrimaryCurrency {
  walletId: number;
  currency: Currency;
}

function getVisibleWallets(wallets: Wallet[], currencyId: number): Wallet[] {
  return wallets.filter(
    (wallet) => wallet.currency_id === currencyId && wallet.is_visible !== false
  );
}

function findWalletForCurrency(wallets: Wallet[], currencyId: number): Wallet | undefined {
  const visibleWallets = getVisibleWallets(wallets, currencyId);

  return visibleWallets.find((wallet) => wallet.type === 'default') ?? visibleWallets[0];
}

function isCurrencyRowActionsTarget(target: EventTarget | null) {
  return target instanceof Element && !!target.closest('[data-currency-row-actions]');
}

function getCurrencyFlagUrl(flagCode: string) {
  return `https://i.nativeteams.com/assets/app/flags_code/${flagCode}.svg`;
}

const WALLET_SORT_OPTIONS: { value: WalletSort; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'balance_desc', label: 'Highest balance' },
  { value: 'balance_asc', label: 'Lowest balance' },
];

/** Balances shown next to the main one, capped at two per design. */
const MAX_SECONDARY_BALANCES = 2;

export function CurrencySwitcher({
  currency,
  activatedCurrencies,
  availableCurrencies,
  hiddenCurrencies,
  walletSort,
  wallets,
  onCurrencySelect,
  isRequestingWallet,
  walletsLoading,
}: CurrencySwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingPrimary, setPendingPrimary] = useState<PendingPrimaryCurrency | null>(null);
  const setPrimaryWallet = useSetPrimaryWallet();
  const setWalletVisibility = useSetWalletVisibility();
  const updateWalletSort = useUpdateWalletSort();

  const secondaryCurrencies = useMemo(
    () =>
      activatedCurrencies
        .filter((curr) => curr.id !== currency.id)
        .slice(0, MAX_SECONDARY_BALANCES),
    [activatedCurrencies, currency.id]
  );

  const hiddenCurrenciesCount = Math.max(
    activatedCurrencies.length - secondaryCurrencies.length - 1,
    0
  );

  async function handleSelect(currencyCode: string) {
    setIsOpen(false);
    await onCurrencySelect(currencyCode);
  }

  function handleSetPrimary(walletId: number, targetCurrency: Currency) {
    setIsOpen(false);
    setPendingPrimary({ walletId, currency: targetCurrency });
  }

  async function handleHide(targetCurrency: Currency) {
    setIsOpen(false);

    try {
      for (const wallet of getVisibleWallets(wallets, targetCurrency.id)) {
        await setWalletVisibility.mutateAsync({ walletId: wallet.id, isVisible: false });
      }
      toast.success(`${targetCurrency.name} (${targetCurrency.code}) is hidden.`);

      const remainingCurrencies = activatedCurrencies.filter(
        (curr) => curr.id !== targetCurrency.id
      );
      const nextCurrency =
        remainingCurrencies.find((curr) => findWalletForCurrency(wallets, curr.id)?.is_primary) ??
        remainingCurrencies[0];
      if (targetCurrency.id === currency.id && nextCurrency) {
        await onCurrencySelect(nextCurrency.code);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Please try again or contact support.');
    }
  }

  async function handleSortChange(sort: WalletSort) {
    try {
      await updateWalletSort.mutateAsync(sort);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Please try again or contact support.');
    }
  }

  async function handleConfirmPrimary() {
    if (!pendingPrimary) return;

    const primaryCurrencyCode = pendingPrimary.currency.code;

    try {
      await setPrimaryWallet.mutateAsync(pendingPrimary.walletId);
      toast.success(`${pendingPrimary.currency.name} is now your primary currency.`);
      setPendingPrimary(null);
      await onCurrencySelect(primaryCurrencyCode);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Please try again or contact support.';
      toast.error(`Failed to set primary currency: ${errorMessage}`);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {secondaryCurrencies.map((curr) => {
        const wallet = findWalletForCurrency(wallets, curr.id);

        return (
          <button
            key={curr.id}
            type="button"
            onClick={() => handleSelect(curr.code)}
            disabled={walletsLoading}
            className="bg-grey-0 hover:bg-grey-50 dark:bg-grey-900 dark:hover:bg-grey-800 flex min-w-0 cursor-pointer items-center gap-2 rounded-lg p-3"
          >
            <Image
              src={getCurrencyFlagUrl(curr.flag_code)}
              alt={`${curr.name} Flag`}
              className="h-4 w-4 shrink-0 rounded-full"
              width={16}
              height={16}
            />
            <p className="truncate text-sm leading-4 font-medium text-neutral-200 dark:text-neutral-100">
              {formatAmount(wallet?.available_balance ?? 0)} {curr.code}
            </p>
          </button>
        );
      })}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Show all currencies"
            disabled={walletsLoading}
            className="bg-grey-0 hover:bg-grey-50 dark:bg-grey-900 dark:hover:bg-grey-800 flex cursor-pointer items-center justify-center rounded-lg px-2 py-3"
          >
            {hiddenCurrenciesCount > 0 ? (
              <p className="text-sm leading-4 font-medium text-neutral-200 dark:text-neutral-100">
                +{hiddenCurrenciesCount}
              </p>
            ) : (
              <ChevronDown className="text-neutral-200 h-4 w-4 dark:text-neutral-100" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[450px] w-[335px] min-w-[335px] gap-4 p-6"
          onInteractOutside={(event) => {
            if (isCurrencyRowActionsTarget(event.target)) event.preventDefault();
          }}
          onFocusOutside={(event) => {
            if (isCurrencyRowActionsTarget(event.target)) event.preventDefault();
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-grey-600 text-sm">Sort by</p>
            <Select
              value={walletSort}
              onValueChange={(value) => handleSortChange(value as WalletSort)}
              disabled={updateWalletSort.isPending || walletsLoading}
            >
              <SelectTrigger
                className="h-9 min-w-[188px] rounded-xl px-3 py-2.5 text-sm"
                hasSelected
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-currency-row-actions="" className="z-[1000]">
                {WALLET_SORT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex max-h-[260px] flex-col gap-4 overflow-y-auto">
            {activatedCurrencies.map((curr) => {
              const wallet = findWalletForCurrency(wallets, curr.id);
              const isPrimary = wallet?.is_primary === true;
              const isSelected = curr.id === currency.id;

              return (
                <div
                  key={curr.id}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-2xl p-3',
                    isSelected
                      ? 'bg-grey-25 dark:bg-grey-900'
                      : 'hover:bg-grey-25 dark:hover:bg-grey-900'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(curr.code)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
                  >
                    <Image
                      src={getCurrencyFlagUrl(curr.flag_code)}
                      alt={`${curr.name} Flag`}
                      className="h-6 w-6 shrink-0 rounded-full"
                      width={24}
                      height={24}
                    />
                    <div className="flex min-w-0 flex-col gap-[7px]">
                      <p className="truncate text-base leading-4 font-normal text-neutral-200 dark:text-neutral-100">
                        {curr.name} ({curr.code})
                      </p>
                      <p className="text-grey-600 truncate text-[10px] leading-none">
                        Available:{' '}
                        <span className="font-medium">
                          {formatAmount(wallet?.available_balance ?? 0)} {curr.code}
                        </span>
                      </p>
                    </div>
                  </button>
                  {isPrimary && (
                    <Badge
                      variant="filled-light-pink"
                      size="m"
                      text="Primary"
                      className="max-h-[20px] shrink-0 px-2 py-0.5"
                      textClassName="text-[10px]"
                    />
                  )}
                  {!isPrimary && wallet && (
                    <CurrencyRowActions
                      onSetPrimary={() => handleSetPrimary(wallet.id, curr)}
                      onHide={() => handleHide(curr)}
                      canHide={getVisibleWallets(wallets, curr.id).every(
                        (visibleWallet) => Number(visibleWallet.available_balance) === 0
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <AddNewCurrencyDialog
            allCurrencies={availableCurrencies}
            currentCurrency={currency}
            onCurrencySelect={handleSelect}
            isLoading={isRequestingWallet}
          >
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-10 w-full px-8"
              disabled={isRequestingWallet || walletsLoading || availableCurrencies.length === 0}
            >
              Add new currency
            </Button>
          </AddNewCurrencyDialog>

          <AddNewCurrencyDialog
            allCurrencies={hiddenCurrencies}
            onCurrencySelect={handleSelect}
            isLoading={isRequestingWallet}
            title="Hidden currencies"
            description="Select a currency to show in your wallet. You’ll then be able to hold and manage funds in that currency."
            submitText="Unhide currency"
            loadingText="Unhiding"
          >
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-10 w-full px-8"
              disabled={isRequestingWallet || walletsLoading || hiddenCurrencies.length === 0}
            >
              Hidden currencies
            </Button>
          </AddNewCurrencyDialog>
        </PopoverContent>
      </Popover>

      <AlertDialog
        open={!!pendingPrimary}
        onOpenChange={(open) => {
          if (!open && !setPrimaryWallet.isPending) setPendingPrimary(null);
        }}
        variant="info"
        title="Set primary currency?"
        description="This currency will be set as your primary currency and used as the default currency across your wallet."
      >
        <Button
          type="button"
          variant="primary"
          className="w-full"
          isLoading={setPrimaryWallet.isPending}
          loadingText="Confirming"
          onClick={handleConfirmPrimary}
        >
          Confirm
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          disabled={setPrimaryWallet.isPending}
          onClick={() => setPendingPrimary(null)}
        >
          Close
        </Button>
      </AlertDialog>
    </div>
  );
}

function CurrencyRowActions({
  onSetPrimary,
  onHide,
  canHide,
}: {
  onSetPrimary: () => void;
  onHide: () => void;
  canHide: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="lg"
          className="h-fit! shrink-0 p-0!"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <MoreVertical className="text-grey-800 dark:text-grey-100 h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-currency-row-actions=""
        className="bg-grey-0! outline-grey-100 w-[275px] gap-[22px] rounded-3xl p-[22px] dark:bg-neutral-200! dark:outline-neutral-200"
      >
        <Button
          type="button"
          variant="ghost"
          size="lg"
          className="hover:bg-grey-50 dark:hover:bg-grey-1000 justify-start rounded-2xl px-4 py-[10px] text-sm leading-4 font-semibold text-neutral-200 md:text-base dark:text-neutral-100 hover:dark:text-neutral-100"
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen(false);
            onSetPrimary();
          }}
        >
          <Star className="h-6! w-6! stroke-2! text-neutral-200" />
          <p>Set as primary</p>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="lg"
          disabled={!canHide}
          className="hover:bg-grey-50 dark:hover:bg-grey-1000 justify-start rounded-2xl px-4 py-[10px] text-sm leading-4 font-semibold text-neutral-200 md:text-base dark:text-neutral-100 hover:dark:text-neutral-100"
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen(false);
            onHide();
          }}
        >
          <EyeOff className="h-6! w-6! stroke-2! text-neutral-200" />
          <p>Hide</p>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
